import React, { useEffect } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"

import { Loader } from "shared/ui/Loader/Loader.component"
import { notify } from "shared/ui/Notifications/notify"

import {
  useProjectDetailsQuery,
  ProjectNotFoundError,
} from "features/Projects/api/useProjectsQuery"
import { useUserStore } from "stores/userStore"
import { useProjectStore } from "stores/projectStore"
import { DashboardContainer } from "features/Dashboard"

/**
 * Dashboard route component - Opens one dashboard of a project.
 *
 * Fetches the project via `useProjectDetailsQuery` from the ID in the URL,
 * syncs the result into the project store, and redirects home when the user
 * state is lost or the project does not exist. `?mode=view` opens View mode,
 * anything else Build mode.
 *
 * @component
 * @returns The Dashboard component.
 */
const Dashboard: React.FC = () => {
  const project = useProjectStore((state) => state.project)
  const setProject = useProjectStore((state) => state.setProject)
  const navigate = useNavigate()
  const user = useUserStore((state) => state.user)

  // Extracts the project ID and dashboard name from the URL parameters
  // useParams already decodes the segments.
  const { projectId, name } = useParams<{ projectId: string; name: string }>()
  const [searchParams] = useSearchParams()
  const mode = searchParams.get("mode") === "view" ? "view" : "build"

  const userReady = Boolean(user && user.username && user.unit)
  const detailsQuery = useProjectDetailsQuery(projectId, userReady)

  useEffect(() => {
    if (!userReady) {
      navigate("/home")
    } else if (!projectId) {
      navigate("/home")
      notify.error("Invalid Project", "No project ID provided in the URL.")
    }
  }, [userReady, projectId, navigate])

  // Sync fetched details into the project store used across the app.
  useEffect(() => {
    if (detailsQuery.data) {
      setProject({
        id: detailsQuery.data.id,
        Owner: detailsQuery.data.owner,
        ...detailsQuery.data.metadata,
      })
    }
  }, [detailsQuery.data, setProject])

  // Any load failure returns home; transport errors have already been
  // notified via the query's errorTitle meta.
  useEffect(() => {
    if (!detailsQuery.error) return
    navigate("/home")
    if (detailsQuery.error instanceof ProjectNotFoundError) {
      notify.error(
        "Project Not Found",
        "The project you are looking for does not exist."
      )
    }
  }, [detailsQuery.error, navigate])

  // Hold the loader until the store has the fetched project: consumers
  // capture the store on first mount, and the setProject sync effect runs
  // one tick after data arrives. A background refetch (say on reconnect)
  // keeps the dashboard mounted: unmounting it would drop unsaved edits and
  // open a second engine session.
  const isStoreSynced =
    detailsQuery.data !== undefined && project.id === detailsQuery.data.id

  if (!isStoreSynced) {
    return (
      <div className="absolute left-2/4 top-2/4">
        <Loader fontSize={48} />
      </div>
    )
  }

  return (
    <>
      <title>{`${project.Name} · ${name}`}</title>
      <DashboardContainer name={name ?? ""} mode={mode} />
    </>
  )
}

export default Dashboard
